import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, getTrackByFn, InputObservable } from '@y42/angular/util';
import { ColumnTypeEnum, OperatorEnum } from '@y42/json-schema';
import { SelectOption } from '@y42/util/select-option';
import { map, Observable, tap } from 'rxjs';
import { OperationInputFormService } from '../../../operation-input-form.service';
import { OperatorForm } from '../../../operation-input.model';

@UntilDestroy()
@Component({
  selector: 'y42-operation-input-number-form-group[form]',
  templateUrl: './operation-input-number-form-group.component.html',
  styleUrls: ['../../operation-input-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationInputNumberFormGroupComponent {
  constructor(private readonly formService: OperationInputFormService, private readonly cdRef: ChangeDetectorRef) {
    this.formService.reset$
      .pipe(
        tap(() => this.cdRef.markForCheck()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  @Input() columnType!: ColumnTypeEnum;
  @Input() form!: FormGroup<OperatorForm>;
  @Input() index!: number;
  @Input() enableMoreConditions = true;
  @Input()
  @InputObservable()
  operatorOptions?: Array<SelectOption<OperatorEnum>>;
  operatorOptions$!: Observable<Array<SelectOption<OperatorEnum>> | undefined>;

  readonly operationsFormArray = this.formService.operationsFormArray;

  private readonly defaultOperatorOptions: Array<SelectOption<OperatorEnum>> = [
    { value: OperatorEnum.Equals, label: 'is equal' },
    { value: OperatorEnum.IsNot, label: 'is not equal' },
    { value: OperatorEnum.In, label: 'is one of' },
    { value: OperatorEnum.NotIn, label: 'is none of' },
    { value: OperatorEnum.LessThan, label: 'is less than' },
    { value: OperatorEnum.GreaterThan, label: 'is greater than' },
    { value: OperatorEnum.Between, label: 'is between' },
    { value: OperatorEnum.IsEmpty, label: 'is empty' },
    { value: OperatorEnum.IsNotEmpty, label: 'is not empty' },
  ];
  readonly trackByValueProp = getTrackByFn<{ value: string; text: string }>('value');

  readonly OperatorEnum = OperatorEnum;

  readonly state = connectState(this, {
    operatorOptions: this.operatorOptions$.pipe(map((options) => options ?? this.defaultOperatorOptions)),
  });

  patchValues(value: unknown, valueIndex: number) {
    const values = [...this.form.controls.values.value];
    values[valueIndex] = value;
    this.form.controls.values.setValue(values);
  }
}
